import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AppDispatch } from '../app/store';
import { api, apiWithoutRestriction } from '../utils/utils';

// Define the interface for media files
export interface MediaFile {
    _id: string;
    filePath: string;
    fileType: string;
    created_at: string;
}

// Define the interface for Merkle Tree Details
export interface MerkleTreeDetails {
    owner: string;
    publicKey: string;
    totalCapacity: string;
    capacityConsumed: string;
}

// Define the interface for Merkle Trees
export interface MerkleTree {
    _id: string;
    address: string;
    structure: string;
    created_at: string;
    details: MerkleTreeDetails;
}

// Define the interface for metadata files
export interface MetadataFile {
    _id: string;
    filePath: string;
    fileType: string;
    content: string;
    created_at: string;
}

// Define the interface for the creator of the private key
export interface PrivateKeyCreator {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    picture: string;
    active: boolean;
    verified_email: boolean;
    balance: number;
    vpcBalance: number;
    lastLogin: string | null;
    country: string | null;
}

// Define the interface for Private Keys
export interface PrivateKeyData {
    id: string;
    title: string;
    wallet_address: string;
    created_at: string;
    created_by: PrivateKeyCreator;
}

// Define the initial state
interface CryptoState {
    mediaFiles: MediaFile[];
    fetchMediaFilesStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
    fetchMediaFilesError: string | null;
    isFetchMediaFilesInProgress: boolean;
    merkleTrees: MerkleTree[];
    fetchMerkleTreesStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
    fetchMerkleTreesError: string | null;
    isFetchMerkleTreesInProgress: boolean;
    merkleTreeDetails: MerkleTreeDetails | null;
    fetchMerkleTreeDetailsStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
    fetchMerkleTreeDetailsError: string | null;
    isFetchMerkleTreeDetailsInProgress: boolean;
    metadataFiles: MetadataFile[];
    fetchMetadataFilesStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
    fetchMetadataFilesError: string | null;
    isFetchMetadataFilesInProgress: boolean;
    createMerkleTreeStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
    createMerkleTreeError: string | null;
    isCreateMerkleTreeInProgress: boolean;
    addNFTMetadataStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
    addNFTMetadataError: string | null;
    isAddNFTMetadataInProgress: boolean;
    uploadArweaveFileStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
    uploadArweaveFileError: string | null;
    uploadedFilePath: string | null;
    privateKeys: PrivateKeyData[];
    fetchPrivateKeysStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
    fetchPrivateKeysError: string | null;
    mintAndTransferCNFTStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
    mintAndTransferCNFTError: string | null;
    mintAndTransferCNFTMessage: string | null;
    addPrivateKeyStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
    addPrivateKeyError: string | null;
    renamePrivateKeyStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
    renamePrivateKeyError: string | null;
    renamePrivateKeyId: string | null;
    deletePrivateKeyStatus: string | null;
    deletePrivateKeyError: string | null;
    deletePrivateKeyId: string | null;
    generateImageEffectsStatus?: 'idle' | 'loading' | 'succeeded' | 'failed';
    generateImageEffectsError?: string | null;
    generateNftArtStatus?: 'idle' | 'loading' | 'succeeded' | 'failed';
    generateNftArtError?: string | null;
    generatedNftArtData?: any; // Replace `any` with actual response shape
}

const initialState: CryptoState = {
    mediaFiles: [],
    fetchMediaFilesStatus: 'idle',
    fetchMediaFilesError: null,
    isFetchMediaFilesInProgress: false,
    merkleTrees: [],
    fetchMerkleTreesStatus: 'idle',
    fetchMerkleTreesError: null,
    isFetchMerkleTreesInProgress: false,
    merkleTreeDetails: null,
    fetchMerkleTreeDetailsStatus: 'idle',
    fetchMerkleTreeDetailsError: null,
    isFetchMerkleTreeDetailsInProgress: false,
    metadataFiles: [],
    fetchMetadataFilesStatus: 'idle',
    fetchMetadataFilesError: null,
    isFetchMetadataFilesInProgress: false,
    createMerkleTreeStatus: 'idle',
    createMerkleTreeError: null,
    isCreateMerkleTreeInProgress: false,
    addNFTMetadataStatus: 'idle',
    addNFTMetadataError: null,
    isAddNFTMetadataInProgress: false,
    uploadArweaveFileStatus: 'idle',
    uploadArweaveFileError: null,
    uploadedFilePath: null,
    privateKeys: [],
    fetchPrivateKeysStatus: 'idle',
    fetchPrivateKeysError: null,
    mintAndTransferCNFTStatus: 'idle',
    mintAndTransferCNFTError: null,
    mintAndTransferCNFTMessage: null,
    addPrivateKeyStatus: 'idle',
    addPrivateKeyError: null,
    renamePrivateKeyStatus: 'idle',
    renamePrivateKeyError: null,
    renamePrivateKeyId: null,
    deletePrivateKeyStatus: 'idle',
    deletePrivateKeyError: null,
    deletePrivateKeyId: null,
    generateImageEffectsStatus: 'idle',
    generateImageEffectsError: null,
    generateNftArtStatus: 'idle',
    generateNftArtError: null,
    generatedNftArtData: null,
};


export const generateNftArt = createAsyncThunk<
    string,
    {
        imageUrl: string;
        kind: string | string[];
        size?: number;
        bcolor?: string;
        filter?: number;
        int?: number;
        t?: number;
        b?: number;
        gap?: number;
        max?: number;
        min?: number;
        ero?: number;
        bright?: number;
        algo?: number;
        english_art?: string; // ✅ Add this line
    },
    { rejectValue: string }
>(
    'crypto/generateNftArt',
    async (
        {
            imageUrl,
            kind,
            size = 480,
            bcolor = '0,0,0',
            filter = 0,
            int = 10,
            t = 20,
            b = 1,
            gap = 0,
            max = 0.02,
            min = 0.0005,
            ero = 0,
            bright = 100,
            algo = 2,
            english_art, // ✅ Add here
        },
        { rejectWithValue }
    ) => {
        try {
            const encodedImage = encodeURIComponent(imageUrl);
            const kindList: string[] = Array.isArray(kind) ? kind : [kind];

            const kindParams = kindList.map((k) => `kind=${encodeURIComponent(k)}`).join('&');

            const queryParams = [
                `image=${encodedImage}`,
                kindParams,
                `size=${size}`,
                `bcolor=${encodeURIComponent(bcolor)}`,
                `filter=${filter}`,
                `int=${int}`,
                `t=${t}`,
                `b=${b}`,
                `gap=${gap}`,
                `max=${max}`,
                `min=${min}`,
                `ero=${ero}`,
                `bright=${bright}`,
                `algo=${algo}`,
            ];

            // ✅ Add english_art only if kind includes "english"
            if (kindList.includes("english") && english_art && english_art.trim().length > 0) {
                queryParams.push(`english_art=${encodeURIComponent(english_art.trim())}`);
            }

            const query = queryParams.join('&');


            const response = await api.get(`/generate_nft_art?${query}`);

            const base64 = response.data?.img_base64;
            if (typeof base64 === 'string' && base64.startsWith('data:image/')) {
                return base64;
            }
            return rejectWithValue('Invalid or missing image_base64 in response.');
        } catch (error: any) {
            if (error.response?.data?.detail) {
                return rejectWithValue(error.response.data.detail);
            }
            return rejectWithValue('Failed to generate NFT art.');
        }
    }
);




export const generateImageEffects = createAsyncThunk<
    string[], // return type: array of image URLs
    File,     // argument: input file
    { rejectValue: string }
>(
    'crypto/generateImageEffects',
    async (file, { rejectWithValue }) => {
        const formData = new FormData();
        formData.append("image", file); // ✅ match field name used by Glitch API

        try {
            const res = await fetch("https://excited-pyrite-attack.glitch.me/api/effects", {
                method: "POST",
                body: formData,
            });

            if (!res.ok) {
                const errorText = await res.text();
                return rejectWithValue(`Glitch API Error: ${errorText}`);
            }

            const data = await res.json();
            if (!data.images || !Array.isArray(data.images)) {
                return rejectWithValue("Invalid response format from API");
            }

            return data.images; // ✅ actual response key is 'images'
        } catch (err) {
            return rejectWithValue("Failed to generate image effects");
        }
    }
);


// Async action to delete a private key
export const deletePrivateKey = createAsyncThunk<
    string, // API response type
    { id: string }, // Request parameters
    { dispatch: AppDispatch, rejectValue: string }
>(
    'crypto/deletePrivateKey',
    async ({ id }, { dispatch, rejectWithValue }) => {
        try {
            const response = await api.get(
                `/wallet_delete_pv_key?id=${encodeURIComponent(id)}`
            );

            dispatch(fetchPrivateKeys());

            return response.data.detail; // Expecting: "Private key deleted successfully."
        } catch (error: any) {
            if (error.response && error.response.status === 401) {
                return rejectWithValue('Not authenticated. Please log in.');
            }
            return rejectWithValue('Failed to delete private key.');
        }
    }
);


// Async action to rename a private key
export const renamePrivateKey = createAsyncThunk<
    string, // API response type
    { id: string; title: string }, // Request parameters
    { dispatch: AppDispatch, rejectValue: string }
>(
    'crypto/renamePrivateKey',
    async ({ id, title }, { dispatch, rejectWithValue }) => {
        try {
            const response = await api.get(
                `/wallet_rename_pv_key?id=${encodeURIComponent(id)}&title=${encodeURIComponent(title)}`
            );

            dispatch(fetchPrivateKeys());


            return response.data; // Expecting: "Private key updated successfully!"
        } catch (error: any) {
            if (error.response && error.response.status === 401) {
                return rejectWithValue('Not authenticated. Please log in.');
            }
            return rejectWithValue('Failed to rename private key.');
        }
    }
);



// Async action to add a private key
export const addPrivateKey = createAsyncThunk<
    string, // API response type
    { privateKey: string; title: string }, // Request parameters
    { dispatch: AppDispatch, rejectValue: string }
>(
    'crypto/addPrivateKey',
    async ({ privateKey, title }, { dispatch, rejectWithValue }) => {
        try {
            const response = await api.get(
                `/wallet_save_pv_key?private_key=${encodeURIComponent(privateKey)}&title=${encodeURIComponent(title)}`
            );

            dispatch(fetchPrivateKeys());

            return response.data; // Expecting: "Private key added successfully!"
        } catch (error: any) {

            if (error.response && error.response.data) {
                return rejectWithValue(error.response.data.detail as string);
            }
            return rejectWithValue('Failed to add private key.');
        }
    }
);


// Async action to mint and transfer CNFT
export const mintAndTransferCNFT = createAsyncThunk<
    { status: string; message: string }, // API response type
    { metaDataId: string; privateKeyId: string; recipientAddress: string; merkleTreeAddress: string; count: number }, // Request parameters
    { rejectValue: string }
>(
    'crypto/mintAndTransferCNFT',
    async ({ metaDataId, privateKeyId, recipientAddress, merkleTreeAddress, count }, { rejectWithValue }) => {
        try {
            const response = await api.get(
                `/sol_mint_transfer_cnft_database?metaDataId=${metaDataId}&privateKeyId=${privateKeyId}&recipientAddress=${recipientAddress}&merkleTreeAddress=${merkleTreeAddress}&count=${count}`
            );

            // If API response has status "failed", reject the request
            if (response.data.status === "failed") {
                return rejectWithValue(response.data.message);
            }

            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Minting and transferring CNFT failed.');
        }
    }
);


// Async action to fetch private keys
export const fetchPrivateKeys = createAsyncThunk<
    PrivateKeyData[],
    void,
    { rejectValue: string }
>(
    'crypto/fetchPrivateKeys',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/wallet_list_pv_keys');
            return response.data;
        } catch (error: any) {
            if (error.response && error.response.status === 401) {
                return rejectWithValue('Not authenticated. Please log in.');
            }
            return rejectWithValue('Failed to fetch private keys.');
        }
    }
);

// Async action to fetch media files
export const fetchMediaFiles = createAsyncThunk<
    MediaFile[],
    void,
    { rejectValue: string }
>(
    'crypto/fetchMediaFiles',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiWithoutRestriction.get('/arweave_media_files');
            return response.data;
        } catch (error: any) {
            if (error.response && error.response.data) {
                return rejectWithValue(error.response.data.detail as string);
            }
            return rejectWithValue('Failed to fetch media files');
        }
    }
);

// Async action to fetch Merkle Trees
export const fetchMerkleTrees = createAsyncThunk<
    MerkleTree[],
    void,
    { rejectValue: string }
>(
    'crypto/fetchMerkleTrees',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiWithoutRestriction.get('/sol_merkle_trees?walletAddress=all');
            return response.data;
        } catch (error: any) {
            if (error.response && error.response.data) {
                return rejectWithValue(error.response.data.detail as string);
            }
            return rejectWithValue('Failed to fetch Merkle Trees');
        }
    }
);

// Async action to fetch Merkle Tree Details
export const fetchMerkleTreeDetails = createAsyncThunk<
    MerkleTreeDetails,
    string,
    { rejectValue: string }
>(
    'crypto/fetchMerkleTreeDetails',
    async (merkleTree, { rejectWithValue }) => {
        try {
            const response = await apiWithoutRestriction.get(`/sol_merkle_tree_details?merkleTree=${merkleTree}`);
            return response.data.details;
        } catch (error: any) {
            if (error.response && error.response.data) {
                return rejectWithValue(error.response.data.detail as string);
            }
            return rejectWithValue('Failed to fetch Merkle Tree details');
        }
    }
);

// Async action to fetch metadata files
export const fetchMetadataFiles = createAsyncThunk<
    MetadataFile[],
    void,
    { rejectValue: string }
>(
    'crypto/fetchMetadataFiles',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiWithoutRestriction.get('/arweave_metadata_files');
            return response.data;
        } catch (error: any) {
            if (error.response && error.response.data) {
                return rejectWithValue(error.response.data.detail as string);
            }
            return rejectWithValue('Failed to fetch metadata files');
        }
    }
);


// Async action to create a Merkle Tree
export const createMerkleTree = createAsyncThunk<
    { status: string; message: string },
    { privateKeyId: string; nftCount: number },
    { dispatch: AppDispatch, rejectValue: string }
>(
    'crypto/createMerkleTree',
    async ({ privateKeyId, nftCount }, { dispatch, rejectWithValue }) => {
        try {
            const response = await api.get(
                `/sol_create_merkle_tree?privateKeyId=${privateKeyId}&nftCount=${nftCount}`
            );

            // If API response has status "failed", reject the request
            if (response.data.status === "failed") {
                return rejectWithValue(response.data.message);
            }

            // If successful, fetch updated Merkle trees
            dispatch(fetchMerkleTrees());
            return response.data;
        } catch (error: any) {
            if (error.response && error.response.data) {
                return rejectWithValue(error.response.data.message as string);
            }
            return rejectWithValue('Failed to create Merkle Tree');
        }
    }
);



// **New Async action to add NFT Metadata**
// Async action to add NFT Metadata
export const addNFTMetadata = createAsyncThunk<
    { status: string; result: { filePath: string } }, // API response type
    Record<string, any>, // JSON metadata type
    { dispatch: AppDispatch; rejectValue: string }
>(
    'crypto/addNFTMetadata',
    async (metadata, { dispatch, rejectWithValue }) => {
        try {
            const response = await api.post('/sol_add_nft_metadata', metadata);

            // If successful, fetch updated metadata files
            if (response.data.status === "sucess") {
                dispatch(fetchMetadataFiles());
                return response.data; // Contains filePath
            } else {
                return rejectWithValue('Failed to add NFT metadata');
            }
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to add NFT metadata');
        }
    }
);

// **New Async action to upload file to Arweave**
const maxFileSize = 2 * 1024 * 1024; // 2MB file size limit

interface UploadResult {
    uploadedFileName: string;
    errorMessage: string | null;
}

// **New Async action to upload file to Arweave with validation checks**
export const uploadArweaveFile = createAsyncThunk<
    UploadResult,
    File | null,
    { dispatch: AppDispatch, rejectValue: string }
>(
    'crypto/uploadArweaveFile',
    async (file, { dispatch, rejectWithValue }) => {
        let uploadedFileName = "";
        let errorMessage: string | null = null;

        // Validate file input
        if (!file) {
            return rejectWithValue("No file selected.");
        }
        if (file.type !== "image/jpeg" && file.type !== "image/png") {
            return rejectWithValue("Please select a valid image file (JPEG or PNG).");
        }
        if (file.size >= maxFileSize) {
            return rejectWithValue("File too large to be uploaded.");
        }

        try {
            const formData = new FormData();
            formData.append("file", file);

            const response = await api.post('/sol_arweave_upload_file', formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            if (response.status === 200 && response.data.result?.filePath) {
                uploadedFileName = response.data.result.filePath;
                dispatch(fetchMetadataFiles());
                return { uploadedFileName, errorMessage: null };
            } else {
                errorMessage = `Upload failed with status: ${response.status}`;
                return rejectWithValue(errorMessage);
            }
        } catch (error: any) {
            errorMessage =
                error.response?.status === 413
                    ? "File too large to be uploaded."
                    : "File can't be uploaded.";
            return rejectWithValue(errorMessage);
        }
    }
);



const cryptoSlice = createSlice({
    name: 'crypto',
    initialState,
    reducers: {
        resetFetchMediaFilesStatus: (state) => {
            state.fetchMediaFilesStatus = 'idle';
            state.fetchMediaFilesError = null;
        },
        resetFetchMerkleTreesStatus: (state) => {
            state.fetchMerkleTreesStatus = 'idle';
            state.fetchMerkleTreesError = null;
        },
        resetFetchMerkleTreeDetailsStatus: (state) => {
            state.fetchMerkleTreeDetailsStatus = 'idle';
            state.fetchMerkleTreeDetailsError = null;
        },
        resetCreateMerkleTreeStatus: (state) => {
            state.createMerkleTreeStatus = 'idle';
            state.createMerkleTreeError = null;
        },
        resetAddNFTMetadataStatus: (state) => {
            state.addNFTMetadataStatus = 'idle';
            state.addNFTMetadataError = null;
        },
        resetUploadArweaveFileStatus: (state) => {
            state.uploadArweaveFileStatus = 'idle';
            state.uploadArweaveFileError = null;
            state.uploadedFilePath = null;
        },
        resetFetchPrivateKeysStatus: (state) => {
            state.fetchPrivateKeysStatus = 'idle';
            state.fetchPrivateKeysError = null;
        },
        resetMintAndTransferCNFTStatus: (state) => {
            state.mintAndTransferCNFTStatus = 'idle';
            state.mintAndTransferCNFTError = null;
            state.mintAndTransferCNFTMessage = null;
        },
        resetAddPrivateKeyStatus: (state) => {
            state.addPrivateKeyStatus = 'idle';
            state.addPrivateKeyError = null;
        },
        resetRenamePrivateKeyStatus: (state) => {
            state.renamePrivateKeyStatus = 'idle';
            state.renamePrivateKeyError = null;
            state.renamePrivateKeyId = null;
        },
        resetDeletePrivateKeyStatus: (state) => {
            state.deletePrivateKeyStatus = 'idle';
            state.deletePrivateKeyError = null;
            state.deletePrivateKeyId = null;
        },
        resetGenerateNftArtStatus: (state) => {
            state.generateNftArtStatus = 'idle';
            state.generateNftArtError = null;
            state.generatedNftArtData = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchMediaFiles.pending, (state) => {
            state.fetchMediaFilesStatus = 'loading';
            state.isFetchMediaFilesInProgress = true;
        });
        builder.addCase(fetchMediaFiles.fulfilled, (state, action: PayloadAction<MediaFile[]>) => {
            state.fetchMediaFilesStatus = 'succeeded';
            state.mediaFiles = action.payload;
            state.isFetchMediaFilesInProgress = false;
        });
        builder.addCase(fetchMediaFiles.rejected, (state, action) => {
            state.fetchMediaFilesStatus = 'failed';
            state.fetchMediaFilesError = action.payload ?? 'Failed to fetch media files';
            state.isFetchMediaFilesInProgress = false;
        });
        builder.addCase(fetchMerkleTrees.pending, (state) => {
            state.fetchMerkleTreesStatus = 'loading';
            state.isFetchMerkleTreesInProgress = true;
        });
        builder.addCase(fetchMerkleTrees.fulfilled, (state, action: PayloadAction<MerkleTree[]>) => {
            state.fetchMerkleTreesStatus = 'succeeded';
            state.merkleTrees = action.payload;
            state.isFetchMerkleTreesInProgress = false;
        });
        builder.addCase(fetchMerkleTrees.rejected, (state, action) => {
            state.fetchMerkleTreesStatus = 'failed';
            state.fetchMerkleTreesError = action.payload ?? 'Failed to fetch Merkle Trees';
            state.isFetchMerkleTreesInProgress = false;
        });
        builder.addCase(fetchMerkleTreeDetails.pending, (state) => {
            state.fetchMerkleTreeDetailsStatus = 'loading';
            state.isFetchMerkleTreeDetailsInProgress = true;
        });
        builder.addCase(fetchMerkleTreeDetails.fulfilled, (state, action: PayloadAction<MerkleTreeDetails>) => {
            state.fetchMerkleTreeDetailsStatus = 'succeeded';
            state.merkleTreeDetails = action.payload;
            state.isFetchMerkleTreeDetailsInProgress = false;
        });
        builder.addCase(fetchMerkleTreeDetails.rejected, (state, action) => {
            state.fetchMerkleTreeDetailsStatus = 'failed';
            state.fetchMerkleTreeDetailsError = action.payload ?? 'Failed to fetch Merkle Tree details';
            state.isFetchMerkleTreeDetailsInProgress = false;
        });
        builder.addCase(fetchMetadataFiles.pending, (state) => {
            state.fetchMetadataFilesStatus = 'loading';
            state.isFetchMetadataFilesInProgress = true;
        });
        builder.addCase(fetchMetadataFiles.fulfilled, (state, action: PayloadAction<MetadataFile[]>) => {
            state.fetchMetadataFilesStatus = 'succeeded';
            state.metadataFiles = action.payload;
            state.isFetchMetadataFilesInProgress = false;
        });
        builder.addCase(fetchMetadataFiles.rejected, (state, action) => {
            state.fetchMetadataFilesStatus = 'failed';
            state.fetchMetadataFilesError = action.payload ?? 'Failed to fetch metadata files';
            state.isFetchMetadataFilesInProgress = false;
        });
        builder.addCase(createMerkleTree.pending, (state) => {
            state.createMerkleTreeStatus = 'loading';
            state.isCreateMerkleTreeInProgress = true;
        });
        builder.addCase(createMerkleTree.fulfilled, (state, action: PayloadAction<{ status: string; message: string }>) => {
            state.createMerkleTreeStatus = 'succeeded';
            state.isCreateMerkleTreeInProgress = false;
        });
        builder.addCase(createMerkleTree.rejected, (state, action) => {
            state.createMerkleTreeStatus = 'failed';
            state.createMerkleTreeError = action.payload ?? 'Failed to create Merkle Tree';
            state.isCreateMerkleTreeInProgress = false;
        });
        builder.addCase(addNFTMetadata.pending, (state) => {
            state.addNFTMetadataStatus = 'loading';
            state.isAddNFTMetadataInProgress = true;
        });
        builder.addCase(addNFTMetadata.fulfilled, (state, action: PayloadAction<{ status: string; result: { filePath: string } }>) => {
            state.addNFTMetadataStatus = 'succeeded';
            state.isAddNFTMetadataInProgress = false;
            state.uploadedFilePath = action.payload.result.filePath; // Store uploaded file path
        });
        builder.addCase(addNFTMetadata.rejected, (state, action) => {
            state.addNFTMetadataStatus = 'failed';
            state.addNFTMetadataError = action.payload ?? 'Failed to add NFT metadata';
            state.isAddNFTMetadataInProgress = false;
        });
        builder.addCase(uploadArweaveFile.pending, (state) => {
            state.uploadArweaveFileStatus = 'loading';
        });
        builder.addCase(uploadArweaveFile.fulfilled, (state, action: PayloadAction<UploadResult>) => {
            state.uploadArweaveFileStatus = 'succeeded';
            state.uploadedFilePath = action.payload.uploadedFileName;
        });
        builder.addCase(uploadArweaveFile.rejected, (state, action) => {
            state.uploadArweaveFileStatus = 'failed';
            state.uploadArweaveFileError = action.payload ?? "File upload failed.";
        });
        builder.addCase(fetchPrivateKeys.pending, (state) => {
            state.fetchPrivateKeysStatus = 'loading';
        });
        builder.addCase(fetchPrivateKeys.fulfilled, (state, action: PayloadAction<PrivateKeyData[]>) => {
            state.fetchPrivateKeysStatus = 'succeeded';
            state.privateKeys = action.payload;
        });
        builder.addCase(fetchPrivateKeys.rejected, (state, action) => {
            state.fetchPrivateKeysStatus = 'failed';
            state.fetchPrivateKeysError = action.payload ?? 'Failed to fetch private keys.';
        });
        builder.addCase(mintAndTransferCNFT.pending, (state) => {
            state.mintAndTransferCNFTStatus = 'loading';
            state.mintAndTransferCNFTError = null;
        });
        builder.addCase(mintAndTransferCNFT.fulfilled, (state, action: PayloadAction<{ status: string; message: string }>) => {
            state.mintAndTransferCNFTStatus = 'succeeded';
            state.mintAndTransferCNFTMessage = action.payload.message; // Store the message
        });
        builder.addCase(mintAndTransferCNFT.rejected, (state, action) => {
            state.mintAndTransferCNFTStatus = 'failed';
            state.mintAndTransferCNFTError = action.payload ?? 'Minting and transferring CNFT failed';
        });
        builder.addCase(addPrivateKey.pending, (state) => {
            state.addPrivateKeyStatus = 'loading';
            state.addPrivateKeyError = null;
        });
        builder.addCase(addPrivateKey.fulfilled, (state, action: PayloadAction<string>) => {
            state.addPrivateKeyStatus = 'succeeded';
        });
        builder.addCase(addPrivateKey.rejected, (state, action) => {
            state.addPrivateKeyStatus = 'failed';
            state.addPrivateKeyError = action.payload ?? 'Failed to add private key.';
        });
        builder.addCase(renamePrivateKey.pending, (state, action) => {
            state.renamePrivateKeyStatus = 'loading';
            state.renamePrivateKeyError = null;
            state.renamePrivateKeyId = action.meta.arg.id; // Store the banner_id being deleted

        });
        builder.addCase(renamePrivateKey.fulfilled, (state, action: PayloadAction<string>) => {
            state.renamePrivateKeyStatus = 'succeeded';
        });
        builder.addCase(renamePrivateKey.rejected, (state, action) => {
            state.renamePrivateKeyStatus = 'failed';
            state.renamePrivateKeyError = action.payload ?? 'Failed to rename private key.';
        });

        builder.addCase(deletePrivateKey.pending, (state, action) => {
            state.deletePrivateKeyStatus = 'loading';
            state.deletePrivateKeyError = null;
            state.deletePrivateKeyId = action.meta.arg.id; // Store the banner_id being deleted
        });
        builder.addCase(deletePrivateKey.fulfilled, (state, action: PayloadAction<string>) => {
            state.deletePrivateKeyStatus = 'succeeded';
        });
        builder.addCase(deletePrivateKey.rejected, (state, action) => {
            state.deletePrivateKeyStatus = 'failed';
            state.deletePrivateKeyError = action.payload ?? 'Failed to delete private key.';
        });
        builder.addCase(generateImageEffects.pending, (state) => {
            state.generateImageEffectsStatus = 'loading';
            state.generateImageEffectsError = null;
        });
        builder.addCase(generateImageEffects.fulfilled, (state) => {
            state.generateImageEffectsStatus = 'succeeded';
        });
        builder.addCase(generateImageEffects.rejected, (state, action) => {
            state.generateImageEffectsStatus = 'failed';
            state.generateImageEffectsError = action.payload ?? 'Failed to generate effects';
        });
        builder.addCase(generateNftArt.pending, (state) => {
            state.generateNftArtStatus = 'loading';
            state.generateNftArtError = null;
            state.generatedNftArtData = null;
        });
        builder.addCase(generateNftArt.fulfilled, (state, action: PayloadAction<any>) => {
            state.generateNftArtStatus = 'succeeded';
            state.generatedNftArtData = action.payload;
        });
        builder.addCase(generateNftArt.rejected, (state, action) => {
            state.generateNftArtStatus = 'failed';
            state.generateNftArtError = action.payload ?? 'Failed to generate NFT art.';
        });

    },
});

export const {
    resetFetchMediaFilesStatus,
    resetFetchMerkleTreesStatus,
    resetFetchMerkleTreeDetailsStatus,
    resetCreateMerkleTreeStatus,
    resetAddNFTMetadataStatus,
    resetUploadArweaveFileStatus,
    resetFetchPrivateKeysStatus,
    resetMintAndTransferCNFTStatus,
    resetAddPrivateKeyStatus,
    resetRenamePrivateKeyStatus,
    resetDeletePrivateKeyStatus,
    resetGenerateNftArtStatus,
} = cryptoSlice.actions;

export default cryptoSlice.reducer;